import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogAttributeDefinition } from './catalogAttributes';
import { CatalogAttributeFilters } from './CatalogAttributeFilters';
import { CatalogCategory, categoryDepth, categoryPath, sortedCategories } from './categoryTree';
import styles from './Catalog.module.css';

type CatalogNs = 'products' | 'services';

type CatalogFilterPanelProps = {
  ns: CatalogNs;
  title: string;
  headerActions?: React.ReactNode;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  resultLabel?: string;
  toolbarEnd?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: CatalogCategory[];
  categoryIds: string[];
  onCategoryIdsChange: (categoryIds: string[]) => void;
  status: string;
  onStatusChange: (status: string) => void;
  definitions: CatalogAttributeDefinition[];
  attributeFilters: Record<string, string>;
  onAttributeChange: (attributeId: string, value: string) => void;
  onClearFilters: () => void;
  children: React.ReactNode;
};

const FilterIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
    <path
      d="M4 6.75h16l-5.6 6.55v4.45L9.6 20.2v-6.9L4 6.75Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CatalogFilterPanel: React.FC<CatalogFilterPanelProps> = ({
  ns,
  title,
  headerActions,
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  resultLabel,
  toolbarEnd,
  open,
  onOpenChange,
  categories,
  categoryIds,
  onCategoryIdsChange,
  status,
  onStatusChange,
  definitions,
  attributeFilters,
  onAttributeChange,
  onClearFilters,
  children,
}) => {
  const { t } = useTranslation([ns, 'common']);
  const activeAttributeFilters = useMemo(
    () => Object.entries(attributeFilters).filter(([, value]) => value.trim()),
    [attributeFilters],
  );
  const selectedCategoryIds = useMemo(() => new Set(categoryIds), [categoryIds]);
  const orderedCategories = useMemo(() => sortedCategories(categories), [categories]);
  const activeFilterCount = categoryIds.length + (status ? 1 : 0) + activeAttributeFilters.length;
  const statusOptions = [
    { value: '', label: t('allStatuses', { ns }) },
    { value: 'active', label: t('statusActive', { ns }) },
    { value: 'archived', label: t('statusArchived', { ns }) },
  ];

  useEffect(() => {
    const layout = document.querySelector('.app-layout');
    const main = document.querySelector('.main-content');
    const className = 'overflow-side-panel-open';

    if (open) {
      layout?.classList.add(className);
      main?.classList.add(className);
    } else {
      layout?.classList.remove(className);
      main?.classList.remove(className);
    }

    return () => {
      layout?.classList.remove(className);
      main?.classList.remove(className);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  const chips = [
    ...categoryIds.map(id => ({
      key: `category-${id}`,
      label: categoryPath(categories, id) || t('category', { ns }),
      onRemove: () => onCategoryIdsChange(categoryIds.filter(item => item !== id)),
    })),
    status ? {
      key: 'status',
      label: status === 'archived' ? t('statusArchived', { ns }) : t('statusActive', { ns }),
      onRemove: () => onStatusChange(''),
    } : null,
    ...activeAttributeFilters.map(([attributeId, value]) => {
      const definition = definitions.find(item => item.id === attributeId);
      const displayValue = definition?.fieldType === 'yesNo'
        ? (value === 'true' ? t('yes', { ns }) : t('no', { ns }))
        : value;
      return {
        key: `attr-${attributeId}`,
        label: definition ? `${definition.name}: ${displayValue}` : displayValue,
        onRemove: () => onAttributeChange(attributeId, ''),
      };
    }),
  ].filter(Boolean) as Array<{ key: string; label: string; onRemove: () => void }>;

  const pageClassName = [
    styles.listPage,
    open ? styles.listPagePanelOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={pageClassName}>
      <div className={styles.listStickyHeader}>
        <div className={styles.listHeaderRow}>
          <div className={styles.panelHeaderSlot}>
            <div className={styles.panelHeader} aria-hidden={!open}>
              <div className={styles.panelHeaderTitle}>{t('filters', { ns })}</div>
              <button
                type="button"
                className={`btn btn-secondary ${styles.panelCloseButton}`}
                onClick={() => onOpenChange(false)}
                disabled={!open}
                aria-label={t('close', { ns: 'common' })}
                title={t('close', { ns: 'common' })}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className={styles.listHeaderMain}>
            <div>
              <h2 className={styles.title}>{title}</h2>
            </div>
            {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
          </div>
        </div>
        <div className={styles.toolbarRow}>
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <input
                className={styles.searchInput}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={event => onSearchChange(event.target.value)}
              />
            </div>
            <button
              type="button"
              className={`${styles.filterTrigger} ${open || activeFilterCount > 0 ? styles.filterTriggerActive : ''}`}
              onClick={() => onOpenChange(!open)}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-pressed={open}
              aria-controls={`${ns}-filter-panel`}
              title={t('filters', { ns })}
            >
              <FilterIcon />
              <span className={styles.filterTriggerLabel}>{t('filters', { ns })}</span>
              {activeFilterCount > 0 ? (
                <span className={styles.filterTriggerCount}>{activeFilterCount}</span>
              ) : null}
            </button>
            {chips.length > 0 ? (
              <div className={styles.filterChipRow}>
                {chips.map(chip => (
                  <span key={chip.key} className={styles.filterChip}>
                    <span className={styles.filterChipLabel}>{chip.label}</span>
                    <button
                      type="button"
                      className={styles.filterChipRemove}
                      onClick={chip.onRemove}
                      aria-label={t('remove', { ns: 'common' })}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <div className={styles.toolbarEnd}>
              {resultLabel ? <span className={styles.resultCount}>{resultLabel}</span> : null}
              {toolbarEnd}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.workspace}>
        <aside
          id={`${ns}-filter-panel`}
          className={styles.sidePanel}
          aria-hidden={!open}
          aria-label={t('filters', { ns })}
        >
          <div className={styles.sidePanelInner}>
            <div
              className={styles.sidePanelBody}
              role={open ? 'dialog' : undefined}
              aria-labelledby={`${ns}-filter-title`}
            >
              <div className={styles.filterCard}>
                <div className={styles.filterIntro}>
                  <h3 id={`${ns}-filter-title`} className={styles.filterTitle}>{t('filters', { ns })}</h3>
                  <p className={styles.filterSubtitle}>{t('filtersHint', { ns })}</p>
                </div>

                <div className={styles.filterField}>
                  <span className={styles.filterFieldLabel}>{t('status', { ns })}</span>
                  <div className={styles.statusSegment} role="radiogroup" aria-label={t('status', { ns })}>
                    {statusOptions.map(option => (
                      <button
                        key={option.value || 'all'}
                        type="button"
                        role="radio"
                        aria-checked={status === option.value}
                        data-status={option.value || 'all'}
                        className={`${styles.statusSegmentBtn} ${status === option.value ? styles.statusSegmentBtnActive : ''}`}
                        onClick={() => onStatusChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.filterField}>
                  <span className={styles.filterFieldLabel}>{t('category', { ns })}</span>
                  <p className={styles.filterFieldHint}>{t('categoriesFilterHint', { ns })}</p>
                  {orderedCategories.length === 0 ? (
                    <p className={styles.filterEmptyHint}>{t('emptyCategories', { ns })}</p>
                  ) : (
                    <div className={styles.categoryCheckboxList} role="group" aria-label={t('category', { ns })}>
                      {orderedCategories.map(category => {
                        const inputId = `${ns}-filter-category-${category.id}`;
                        const checked = selectedCategoryIds.has(category.id);
                        return (
                          <label
                            key={category.id}
                            htmlFor={inputId}
                            className={`${styles.categoryCheckbox} ${checked ? styles.categoryCheckboxChecked : ''}`}
                            style={{ paddingLeft: `${12 + categoryDepth(categories, category.id) * 16}px` }}
                          >
                            <input
                              id={inputId}
                              type="checkbox"
                              checked={checked}
                              onChange={() => onCategoryIdsChange(
                                checked
                                  ? categoryIds.filter(item => item !== category.id)
                                  : [...categoryIds, category.id],
                              )}
                            />
                            <span className={styles.categoryCheckboxMark} aria-hidden />
                            <span className={styles.categoryCheckboxLabel}>{category.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className={styles.filterSection}>
                  <div className={styles.filterSectionTitle}>{t('categoryFields', { ns })}</div>
                  {categoryIds.length === 1 ? (
                    definitions.length > 0 ? (
                      <CatalogAttributeFilters
                        ns={ns}
                        definitions={definitions}
                        values={attributeFilters}
                        onChange={onAttributeChange}
                      />
                    ) : (
                      <p className={styles.filterEmptyHint}>{t('noCategoryFields', { ns })}</p>
                    )
                  ) : (
                    <p className={styles.filterEmptyHint}>
                      {categoryIds.length > 1
                        ? t('chooseSingleCategoryForFields', { ns })
                        : t('chooseCategoryForFields', { ns })}
                    </p>
                  )}
                </div>

                <div className={styles.filterFooter}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClearFilters}
                    disabled={activeFilterCount === 0}
                  >
                    {t('clearFilters', { ns: 'common' })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => onOpenChange(false)}
                  >
                    {t('filtersDone', { ns })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>
        <div className={styles.catalogSurface}>
          {children}
        </div>
      </div>
    </div>
  );
};
